import { useTranslation } from 'react-i18next';
import { Alert, Group, Stack, Text, ThemeIcon } from '@mantine/core';
import { IconClock, IconPackageOff } from '@tabler/icons-react';
import ForwardingWeights from '@/components/app/deals/steps/ForwardingWeights';
import PartyIdentity from '@/components/app/deals/steps/PartyIdentity';
import { forwardingPhase, hasForwardingData } from '@/lib/forwarding';
import type { BargainStepsInfo } from '@/types/deals';

interface ForwardingStepProps {
  readonly dealId: number;
  readonly info: BargainStepsInfo;
}

// Шаг «Перевозка» (id 4). До появления данных экспедирования — баннеры ожидания; как только есть данные —
// табовый дашборд (ForwardingWeights) с закреплённой зоной действий (подпись УПД, подтверждение получения).
export default function ForwardingStep({ dealId, info }: ForwardingStepProps) {
  const { t } = useTranslation();
  const phase = forwardingPhase(info.processingStep ?? '');
  const hasData = hasForwardingData(info.forwardingInfo);

  if (phase === 'rejected') {
    return (
      <Alert color="red" variant="light" icon={<IconPackageOff size={18} />}>
        {t('app.deals.forwarding.rejected')}
      </Alert>
    );
  }

  // Данных экспедирования ещё нет. Текст зависит от фазы: идёт поиск экспедитора (он ещё не назначен) /
  // экспедитор приступил, распределяет ТС. Ожидание перевозчика сюда больше не попадает — это отдельный
  // шаг сделки «Назначение перевозчика».
  // Реквизиты экспедитора показываем, когда он уже назначен (в фазе поиска forwarder пуст → блока нет).
  if (!hasData) {
    const waitKey = phase === 'awaitAccept' ? 'app.deals.forwarding.searching' : 'app.deals.forwarding.allocating';
    return (
      <Stack gap="md">
        <Group gap="xs" wrap="nowrap" align="flex-start">
          <ThemeIcon size={32} radius="xl" variant="light" color="brand">
            <IconClock size={18} />
          </ThemeIcon>
          <Text fz="sm">{t(waitKey)}</Text>
        </Group>
        <PartyIdentity party={info.forwarder} />
      </Stack>
    );
  }

  return <ForwardingWeights info={info} dealId={dealId} />;
}
